import type { SDKModule, SDKContext } from '../types';
import { closestFrom, getTextContent } from '../utils/dom';
import { parseTrackConfig, resolveProperties } from '../utils/variable-resolver';

/**
 * Event Tracker Module
 *
 * Delegation on [data-pt-track] elements. Backend's autoConfigureTrackEvents
 * stamps data-pt-track on every button / link / form during publish, so we rely
 * on that contract here and do NOT also auto-track unannotated buttons (Mode B
 * was removed as dead code in production).
 *
 * Resolution flow:
 *   click / submit → nearest eligible [data-pt-track] → parse JSON or legacy
 *   string → resolve variables → ctx.track(eventName, props)
 *
 * ## Forms report on submit, never on click (2026-08-25)
 *
 * Publish stamps `{"eventName":"form_submit"}` on `form[data-pt-id]`, but until now
 * the only listener here was `click`. Two things were wrong at once, and both were
 * silent:
 *   - **Over-report** — page-import annotates `<form>` as an atomic tag and skips its
 *     whole subtree, so no input/label/button inside carries data-pt-track of its own.
 *     `closest()` therefore walked up to the `<form>` on *every* click inside it:
 *     clicking a text field to focus it reported a `form_submit`. Filling six fields
 *     sent six of them before the user ever submitted.
 *   - **Under-report** — the actual submission (Enter inside a field, or a CTA whose
 *     action submits the form) is not a click at all, so it reported nothing.
 *
 * Hence the split: `<form>` is skipped in the click path (walking further up, so a
 * tracked ancestor of the form still works) and handled in the submit path instead.
 *
 * ⚠️ **dynamic-form wrappers are NOT ours** — `[data-pt-component="dynamic-form"]`
 * has its own owner: the editor's inline script preventDefaults the native submit and
 * dispatches `pt-form-submit`, which form-handler.ts turns into a `form_submit` call.
 * Its inner `<form>` carries `data-pt-id` (ComponentRenderers writes it), so publish
 * stamps data-pt-track on it too — reporting it here as well would send **two**
 * `form_submit` per submission. Our submit listener is capture-phase, so it fires
 * before that preventDefault and the duplicate would not even be suppressible.
 *
 * Known blind spot (unchanged, and not fixable without monkey-patching): programmatic
 * `form.submit()` fires no submit event, so it reports nothing. Constraint-validation
 * failures also report nothing — which is correct, nothing was submitted.
 */
export function createEventTracker(): SDKModule {
  let ctx: SDKContext;
  let clickHandler: ((e: Event) => void) | null = null;
  let submitHandler: ((e: Event) => void) | null = null;

  const isForm = (el: Element): boolean => el.tagName === 'FORM';

  /** Nearest [data-pt-track] at or above `target`, skipping `<form>` (see header) */
  function trackedForClick(target: EventTarget | null): Element | null {
    let el = closestFrom(target, '[data-pt-track]');
    while (el && isForm(el)) {
      el = el.parentElement ? closestFrom(el.parentElement, '[data-pt-track]') : null;
    }
    return el;
  }

  function report(tracked: Element): void {
    const raw = tracked.getAttribute('data-pt-track');
    const config = parseTrackConfig(raw);
    if (!config || !config.eventName) return;

    const properties: Record<string, unknown> = resolveProperties(config, tracked);

    // Always include standard context
    const ptId = tracked.getAttribute('data-pt-id') || '';
    if (!properties.label) {
      properties.label = getTextContent(tracked);
    }
    if (!properties.id && ptId) {
      properties.id = ptId;
    }

    ctx.track(config.eventName, properties);
  }

  function handleClick(e: Event): void {
    const tracked = trackedForClick(e.target as EventTarget | null);
    if (!tracked) return;
    report(tracked);
  }

  function handleSubmit(e: Event): void {
    const target = e.target as Element | null;
    if (!target || !target.getAttribute) return;
    // Only the submitting form itself — never a tracked ancestor of it (an outer
    // banner's click event has nothing to do with this form being submitted)
    if (!isForm(target) || !target.hasAttribute('data-pt-track')) return;
    // dynamic-form has its own reporter (see header ⚠️)
    if (closestFrom(target, '[data-pt-component="dynamic-form"]')) return;
    report(target);
  }

  return {
    name: 'event-tracker',
    init(context: SDKContext): void {
      ctx = context;
      clickHandler = handleClick;
      submitHandler = handleSubmit;
      document.addEventListener('click', clickHandler, true);
      // Capture phase: AJAX forms preventDefault in their own submit handler, and a
      // submission that the page handles itself is still a submission worth reporting.
      document.addEventListener('submit', submitHandler, true);
    },
    destroy(): void {
      if (clickHandler) {
        document.removeEventListener('click', clickHandler, true);
        clickHandler = null;
      }
      if (submitHandler) {
        document.removeEventListener('submit', submitHandler, true);
        submitHandler = null;
      }
    },
  };
}
